import React from 'react'
import cx from 'classnames'
import styles from './CardMedia.module.scss'
import Image from 'next/image'
import { Col, Container, Row } from 'react-grid-system'
import { Button, Heading, ReadMoreField, Vimeo } from '~elements'
import { ImageProps, VimeoProps } from '~types'
import parse from 'html-react-parser'
import { gaBtnClick } from 'utils/hooks/gaBtnClick'
import { CTA_CLICK_EVENT } from '~analyticsConstants'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { useSmoothAnchorScroll } from 'utils/hooks/useSmoothAnchorScroll'

interface CardMediaProps {
  copy?: { html: string }
  ctaBtnUrl?: string
  ctaBtnText?: string
  ctaBtnIsTargetBlank?: boolean
  ctaBtnColor?: string
  hasMediaInRightColumn?: boolean
  headline?: string
  isChildOfGrid?: boolean
  items?: Array<CardMediaProps>
  mediaColumn: Array<MediaItem>
  subheadline: string
  imgSubheadline: ImageProps
  anchorId: string
  zIndex?: number
  readMoreField?: { html: string }
}
type MediaItem = ImageProps | VimeoProps
// narrow MediaItem → ImageProps
const isImageMedia = (m: MediaItem): m is ImageProps => m.componentName === 'Image'

// narrow MediaItem → VimeoProps
const isVimeoMedia = (m: MediaItem): m is VimeoProps => m.componentName === 'Vimeo'

const CardMediaItem = ({ item, isLastChild }: { item: CardMediaProps; isLastChild?: boolean }) => {
  const {
    copy,
    ctaBtnUrl,
    ctaBtnText,
    ctaBtnIsTargetBlank,
    ctaBtnColor,
    hasMediaInRightColumn,
    headline,
    subheadline,
    imgSubheadline,
    mediaColumn,
    anchorId,
    readMoreField
  } = item || {}

  const cardMediaStyles = cx([
    styles['card-media'],
    styles[isLastChild ? 'is-last-child' : ''],
    styles[mediaColumn && isVimeoMedia(mediaColumn[0]) && mediaColumn[0]?.posterImage ? 'has-vimeo-modal' : ''],
    styles[`has-media-in-${hasMediaInRightColumn ? 'right' : 'left'}-col`]
  ])

  const { ref: cardRef, shouldSetId } = useSmoothAnchorScroll(anchorId)
  let mediaImageUrl = ''
  if (mediaColumn && mediaColumn.length > 0) {
    const imageMedia = mediaColumn.find(isImageMedia)
    if (imageMedia && imageMedia?.image && imageMedia.image?.url) {
      mediaImageUrl = imageMedia.image.url
    }
  }

  const useMediaImageUrl =
    (ctaBtnText && ctaBtnText.toLowerCase() === 'view map') || (ctaBtnUrl && ctaBtnUrl.startsWith('https://media.graphassets.com'))

  const buttonUrl = useMediaImageUrl && mediaImageUrl ? mediaImageUrl : ctaBtnUrl

  return (
    <Col sm={12}>
      <div className={styles['scroll']} {...(shouldSetId && { id: anchorId })} ref={cardRef}>
        <Row className={cardMediaStyles}>
          <Col lg={6}>
            <div className={styles['col-wrapper--media']}>
              {mediaColumn &&
                mediaColumn.map(media => (
                  <div className={styles['media-column']} key={isImageMedia(media) ? media.id : media.url}>
                    {isImageMedia(media) && media?.image?.url ? (
                      <div className={'image-container'}>
                        <Image alt={media?.fileName} src={media.image.url} sizes='100%' fill quality={NEXT_IMAGE_QUALITY} />
                      </div>
                    ) : isVimeoMedia(media) ? (
                      <Vimeo {...media} />
                    ) : null}
                  </div>
                ))}
            </div>
          </Col>
          <Col lg={6} className='align-contents-vertical'>
            <div className={styles['col-wrapper--content']}>
              {headline && (
                <Heading level={2}>
                  <>{headline}</>
                </Heading>
              )}
              {subheadline && (
                <Heading level={3} color='light'>
                  {subheadline}
                </Heading>
              )}
              <ReadMoreField content={readMoreField} />
              {/* currently not using Image component as width and height is different per img need to refactor */}
              {imgSubheadline?.image?.url && <img alt={imgSubheadline?.alt} className={styles['img-subheadline']} src={imgSubheadline.image.url} />}
              {copy?.html && parse(copy.html)}
              {ctaBtnText && ctaBtnUrl && (
                <Button
                  onClick={() => gaBtnClick(CTA_CLICK_EVENT, headline || '', ctaBtnText)}
                  color={ctaBtnColor || 'light'}
                  fill='solid'
                  title={ctaBtnText}
                  url={buttonUrl}
                  isTargetBlank={ctaBtnIsTargetBlank}
                />
              )}
            </div>
          </Col>
        </Row>
      </div>
    </Col>
  )
}

/*
 * @TODO: revisit to determine if we need Card Grids
 * Added extra props below to render a single card in case an array of cards is recieved
 * @TODO zIndex is hack way to fix partnership page cardmedia
 */
const CardMedia = ({
  items,
  copy,
  ctaBtnUrl,
  ctaBtnText,
  ctaBtnIsTargetBlank,
  ctaBtnColor,
  hasMediaInRightColumn,
  headline,
  mediaColumn,
  subheadline,
  imgSubheadline,
  anchorId,
  zIndex,
  readMoreField
}: CardMediaProps) => {
  const cardMediaItem = {
    copy,
    ctaBtnUrl,
    ctaBtnText,
    ctaBtnIsTargetBlank,
    ctaBtnColor,
    hasMediaInRightColumn,
    headline,
    subheadline,
    imgSubheadline,
    mediaColumn,
    anchorId,
    readMoreField
  }
  return (
    <>
      {/* If an array of cards is recieved, maps the array, otherwise return a single card */}
      {items && items.length > 0 ? (
        items.map((item, index) => <CardMediaItem item={item} key={index} isLastChild={items.length - 1 === index} />)
      ) : (
        <Container className={'container container--narrow-xxl'} {...(zIndex === 0 && { style: { zIndex } })}>
          <CardMediaItem item={cardMediaItem} />
        </Container>
      )}
    </>
  )
}
export default CardMedia
